import axios, { AxiosError, AxiosPromise, AxiosRequestConfig } from 'axios';

import AuthStore from '@app/src/common/store/AuthStore';
import routerStore from '@app/src/common/store/RouterStore';
import ROUTES from '@app/src/common/constants/routeUrls';
import { getLocationRequestHeader } from '@app/src/common/repository/locationRequestHeader';

// Endpoints reached before any token exists: they must not receive an Authorization or
// X-Location-Id header, stale or otherwise.
export const UNAUTHENTICATED_URLS = [
  '/api/user-management/auth/signin',
  '/api/user-management/auth/signin-verify',
  '/api/user-management/auth/signin-resend',
  '/api/user-management/auth/refresh'
];

axios.interceptors.request.use(
  config => {
    const signInUrls = UNAUTHENTICATED_URLS;
    if (config.url && !signInUrls.includes(config.url)) {
      config.headers.Authorization = AuthStore.tokenStrJwt || false;
    }
    if (!signInUrls.includes(config.url!) && !config.headers['X-Location-Id']) {
      const locationId = getLocationRequestHeader();
      if (locationId) {
        config.headers['X-Location-Id'] = locationId;
      }
    }
    return config;
  },
  error => error
);

export interface ITableRequestParams {
  startIndex?: number;
  endIndex?: number;
  sort?: string;
  search?: string;
  status?: string;
  eventStart?: string;
  eventEnd?: string;
  displayStart?: string;
  displayEnd?: string;
  statuses?: string;
  scope?: number;
  filter?: string | {}; // {} - because of MonitoringRequestParams
}

export interface IEnumerableResponse<T> {
  items: T[];
  total: number;
}

// TODO check error statuses
export abstract class BaseRepository {
  protected baseUrl: string = `/api`;
  protected ereceptionstUrl: string = `${this.baseUrl}/ereceptionist`;
  protected fileStorageUrl: string = `${this.baseUrl}/file-storage`;
  protected userManagementUrl: string = `${this.baseUrl}/user-management`;
  protected credentialsManagementUrl: string = `${this.baseUrl}/credentials-management`;
  protected deviceManagementUrl: string = `${this.baseUrl}/device-management`;
  protected projectManagementUrl: string = `${this.baseUrl}/project-management`;
  protected scheduleManagementUrl: string = `${this.baseUrl}/schedule-management`;
  protected orderManagementUrl: string = `${this.baseUrl}/order-management`;
  protected wayFindingUrl: string = `${this.baseUrl}/way-finding`;
  protected notificationUrl: string = `${this.baseUrl}/notification-management`;
  protected roomBookingUrl: string = `${this.baseUrl}/roombooking`;
  protected contentFolders: string = `${this.baseUrl}/ContentFolders`;
  protected auditLogs: string = `${this.baseUrl}/auditlog-management`;

  protected callGet<T, P = any>(
    url: string,
    hashParams?: P,
    config?: AxiosRequestConfig
  ): AxiosPromise<T> {
    return AuthStore.checkAccess()
      .then(token =>
        axios.get<T>(`${url}${this.constructHashString(hashParams)}`, {
          ...config,
          headers: {
            ...config?.headers,
            authorization: token.jwtToken || false
          }
        })
      )
      .then(r => r, this.handleBadRequest);
  }

  protected callPost<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): AxiosPromise<T> {
    return AuthStore.checkAccess()
      .then(token =>
        axios.post<T>(url, data, {
          ...config,
          headers: {
            authorization: token.jwtToken || false
          }
        })
      )
      .then(r => r, this.handleBadRequest);
  }

  protected callPostWithHeaders<T>(
    url: string,
    data?: any,
    headers?: any
  ): AxiosPromise<T> {
    return AuthStore.checkAccess()
      .then(token =>
        axios.post<T>(url, data, {
          headers: {
            ...headers,
            authorization: token.jwtToken || false
          }
        })
      )
      .then(r => r, this.handleBadRequest);
  }

  protected callPut<T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): AxiosPromise<T> {
    return AuthStore.checkAccess()
      .then(token =>
        axios.put<T>(url, data, {
          headers: {
            authorization: token.jwtToken || false
          },
          ...config
        })
      )
      .then(r => r, this.handleBadRequest);
  }

  protected callDelete(url: string, data?: any): AxiosPromise {
    return AuthStore.checkAccess()
      .then(token =>
        axios.delete(url, {
          headers: {
            authorization: token.jwtToken || false
          },
          data
        })
      )
      .then(r => r, this.handleBadRequest);
  }

  protected callDeleteWithParams<P = any>(
    url: string,
    hashParams?: P
  ): AxiosPromise {
    return AuthStore.checkAccess()
      .then(token =>
        axios.delete(`${url}${this.constructHashString(hashParams)}`, {
          headers: {
            authorization: token.jwtToken || false
          }
        })
      )
      .then(r => r, this.handleBadRequest);
  }

  protected constructHashString<T>(params?: T): string {
    if (!params) {
      return '';
    }
    const keyList: string[] = Object.keys(params);
    if (!keyList.length) {
      return '';
    }
    return (
      '?' +
      keyList
        .filter(key => params[key] !== null && params[key] !== void 0)
        .map(key => {
          if (Array.isArray(params[key])) {
            return params[key].map((el: any) => `${key}=${el}`).join('&');
          }

          return `${key}=${params[key]}`;
        })
        .join('&')
    );
  }

  protected handleBadRequest = (response: {
    response: { code: string; status: number };
    message?: string;
    code?: string;
  }): any => {
    if (response?.code === AxiosError.ECONNABORTED) {
      throw response;
    }
    if (response?.code === AxiosError.ERR_CANCELED) {
      return response;
    }

    if (
      !response ||
      (!response.response && response.message !== 'Network Error')
    ) {
      routerStore.gotoAuthentication();
      return response;
    }
    switch (response.response?.status) {
      /*      case 400:
        notificationService.serverErrorMessage(response.response.code);
        return response;*/
      case 401:
        AuthStore.clearData();
        routerStore.gotoAuthentication();
        return response;
      case 500:
        routerStore.navigate(ROUTES.serverErrorPage.path);
        break;
      /*       case 404:
        routerStore.navigate(ROUTES.notFoundPage.path);
        break; */
      default:
    }
    throw response;
  };
}
